import { useState, FormEvent } from 'react';

export default function ProjectForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: ''
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6krbl94a3uojp5fg210', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });

      if (!response.ok) throw new Error('Submission failed');

      setFormData({ name: '', email: '', phone: '', business: '', message: '' });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="start-project" className="py-32 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-12 text-center shadow-xl shadow-neutral-200/30">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-green-600"></i>
            </div>
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">
              Thanks — we'll get back to you shortly.
            </h2>
            <p className="text-lg text-neutral-500 mb-8">
              We've received your project inquiry and will be in touch within 24 hours.
            </p>
            <p className="text-neutral-600 mb-6">
              Want to get started faster?
            </p>
            <a 
              href="tel:+16316407888"
              className="inline-flex items-center gap-3 bg-neutral-900 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-neutral-800 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Call (631) 640-7888
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="start-project" className="py-32 bg-neutral-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Info */}
          <div className="lg:sticky lg:top-24">
            <p className="text-sm font-medium text-[#1E3A8A] tracking-wide mb-4">
              GET STARTED
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-neutral-900 tracking-tight mb-6">
              Let's grow your business together
            </h2>
            <p className="text-lg text-neutral-500 mb-8">
              Tell us about your project and we'll get back to you within 24 hours. Or call us directly for an immediate consultation.
            </p>

            <a 
              href="tel:+16316407888"
              className="inline-flex items-center gap-4 bg-neutral-900 text-white px-8 py-5 rounded-2xl hover:bg-neutral-800 transition-all cursor-pointer group mb-8"
            >
              <div className="w-12 h-12 rounded-full bg-[#1E3A8A] flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-phone-fill text-white text-xl"></i>
              </div>
              <div className="text-left">
                <p className="text-sm text-neutral-400">Call us now</p>
                <p className="text-xl font-semibold whitespace-nowrap">(631) 640-7888</p>
              </div>
            </a>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-neutral-600">
                <i className="ri-check-double-line text-[#1E3A8A] text-lg"></i>
                <span className="text-sm">Free consultation for Long Island businesses</span>
              </div>
              <div className="flex items-center gap-3 text-neutral-600">
                <i className="ri-check-double-line text-[#1E3A8A] text-lg"></i>
                <span className="text-sm">Response within 24 hours</span>
              </div>
              <div className="flex items-center gap-3 text-neutral-600">
                <i className="ri-check-double-line text-[#1E3A8A] text-lg"></i>
                <span className="text-sm">No obligation quote</span>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-neutral-200/30">
            <form 
              id="project-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] transition-all text-sm"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] transition-all text-sm"
                    placeholder="john@business.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] transition-all text-sm"
                    placeholder="(631) 555-1234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Business Name
                  </label>
                  <input
                    type="text"
                    name="business"
                    value={formData.business}
                    onChange={(e) => setFormData({ ...formData, business: e.target.value })}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] transition-all text-sm"
                    placeholder="Your Business Name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Tell Us About Your Project
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] transition-all text-sm resize-none"
                  placeholder="What are you looking to achieve? Any specific goals or timeline?"
                />
                <p className="text-xs text-neutral-400 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-neutral-900 text-white py-4 rounded-xl text-sm font-medium hover:bg-neutral-800 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    Submit Project Inquiry
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>

              <p className="text-xs text-neutral-400 text-center">
                By submitting, you agree to be contacted about your project. We respect your privacy.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
